import { useState } from "react";
import { Input, Button, Avatar, Divider, Select, Tag } from "antd";
import { SearchOutlined, UserAddOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Employee, EmployeeTag, Permission } from "./types";

interface EmployeeListProps {
  employees: Employee[];
  permissions?: Permission[];
  allTags?: EmployeeTag[];
  onSelectEmployee: (employee: Employee) => void;
  onAddEmployee: () => void;
}

function EmployeeCard({
  employee,
  onClick,
  resigned,
}: {
  employee: Employee;
  onClick: () => void;
  resigned?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: 16,
        backgroundColor: resigned ? "#fafafa" : "#f5f5f5",
        borderRadius: 8,
        cursor: "pointer",
        transition: "background-color 0.2s",
        opacity: resigned ? 0.65 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = resigned ? "#f0f0f0" : "#e8e8e8";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = resigned ? "#fafafa" : "#f5f5f5";
      }}
    >
      <Avatar
        size={56}
        src={employee.avatar}
        style={{
          backgroundColor: resigned ? "#bfbfbf" : "#d9d9d9",
          color: "#666",
          fontSize: 20,
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {employee.name.slice(0, 1)}
      </Avatar>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: resigned ? "#999" : undefined,
          }}
        >
          {employee.name}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#999",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {employee.email}
        </div>
        <div style={{ fontSize: 13, color: resigned ? "#bfbfbf" : "#1677ff" }}>
          {employee.birthday}
        </div>
        {(employee.tags ?? []).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
            {(employee.tags ?? []).map((tag) => (
              <Tag key={tag.id} color={resigned ? "default" : "blue"} style={{ fontSize: 11, margin: 0 }}>
                {tag.name}
              </Tag>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmployeeList({ employees, allTags, onSelectEmployee, onAddEmployee }: EmployeeListProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagId, setSelectedTagId] = useState<string | undefined>(undefined);

  const matchesSearch = (emp: Employee) =>
    emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchQuery.toLowerCase());

  const matchesTag = (emp: Employee) =>
    !selectedTagId || (emp.tags ?? []).some((t) => t.id === selectedTagId);

  const activeEmployees = employees.filter(
    (emp) => emp.status !== "resigned" && matchesSearch(emp) && matchesTag(emp),
  );

  const resignedEmployees = employees.filter(
    (emp) => emp.status === "resigned" && matchesSearch(emp) && matchesTag(emp),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", gap: 8, flex: 1 }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#999" }} />}
            placeholder={t("employee.list.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ maxWidth: 360 }}
            allowClear
          />
          <Select
            allowClear
            placeholder={t("employee.list.filterByTag")}
            value={selectedTagId}
            onChange={(val) => setSelectedTagId(val)}
            style={{ minWidth: 140 }}
            options={(allTags ?? []).map((t) => ({ label: t.name, value: t.id }))}
            notFoundContent={t("employee.list.noTags")}
          />
        </div>
        <Button type="primary" icon={<UserAddOutlined />} onClick={onAddEmployee}>
          {t("employee.management.addEmployee")}
        </Button>
      </div>

      <div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: "#555" }}>
          {t("employee.list.activeSection")}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeEmployees.map((employee) => (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              onClick={() => onSelectEmployee(employee)}
            />
          ))}
        </div>
        {activeEmployees.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#999" }}>
            {t("employee.list.noResults")}
          </div>
        )}
      </div>

      {(resignedEmployees.length > 0 || searchQuery) && (
        <>
          <Divider style={{ margin: "0" }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: "#999" }}>
              {t("employee.list.resignedSection")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resignedEmployees.map((employee) => (
                <EmployeeCard
                  key={employee.id}
                  employee={employee}
                  onClick={() => onSelectEmployee(employee)}
                  resigned
                />
              ))}
            </div>
            {resignedEmployees.length === 0 && (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#bfbfbf", fontSize: 13 }}>
                {t("employee.list.noResigned")}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default EmployeeList;
